import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import type { CustomEmberTableRow } from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { tKey } from 'ember-intl';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type {
  ParityManagerEscalationinfo,
  ParityManagerFiltersMarketInfo,
  ParityManagerIssue,
  ParityManagerIssuestat,
  ParityManagerIssuesummary,
} from 'lumen/models';
import {
  ISSUE_TYPE_PRETTY_NAMES,
  ISSUE_TYPES,
  type IssueType,
} from 'lumen/models/parity-manager/issue';
import type MarketParityParityManagerRoute from 'lumen/routes/market/parity/parity-manager';
import type { Filters } from 'lumen/routes/market/parity/parity-manager';
import type AjaxService from 'lumen/services/ajax';
import type { DrawerID } from 'lumen/services/drawer';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  APIGATEWAY,
  DRAWER_IDS,
} from 'lumen/utils/constants/general-constants';
import type { SelectInputOptionWithId } from 'lumen/utils/constants/pi-constant-values';
import {
  MAIL_TYPE,
  PM_DATA_START,
} from 'lumen/utils/constants/pm-constant-values';
import { logReportInput } from 'lumen/utils/log-report-input';
import { formatUrl } from 'lumen/utils/rates-request';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface IssueRequestParams {
  assignee: number[];
  entry_mode: number[];
  escalation_level: number[];
  extract_date_end: string;
  extract_date_start: string;
  issue_type: IssueType;
  market_id?: string;
  namespace: typeof APIGATEWAY;
  ota_id: string[] | string;
  root_cause: number[];
  search_query: string;
  status: number[];
  subscription_id?: string;
  typeKey: string;
}

interface ExportRequestParams {
  assignee?: number[];
  entry_mode?: number[];
  escalation_level?: number[];
  extract_date_end?: string;
  extract_date_start?: string;
  issue_type: IssueType;
  market_id: string;
  ota_id?: string[];
  report: string;
  root_cause?: number[];
  search_query: string;
  status?: number[];
}

interface StatsResult {
  escalationinfos: ParityManagerEscalationinfo[];
  issuestats?: ParityManagerIssuestat[];
  issuesummaries: ParityManagerIssuesummary[];
}

export interface MetricOption {
  amountIssues?: number;
  drawerTitle: string;
  id: IssueType;
  isLoading: boolean;
  isSelected: boolean;
  metricTitle: string;
  numSubscriptions?: number;
  sidebarTitle: string;
  tableTitle: string;
}

type FilterOption = SelectInputOptionWithId<string | number>;

const DROPDOWN_OPTION_NOT_SET = 0;

// Helper function that selects all options by default if value is an empty array
// Otherwise it will look for the selected options in the options array and return them
const findOptionsForValue = (
  value: string[],
  options: FilterOption[],
): FilterOption[] => {
  if (value.length === 0) {
    return options;
  }

  return value
    .map((selectedOption) =>
      options.find((option) => option['id'] === selectedOption),
    )
    .filter(Boolean) as FilterOption[];
};

const mapToOption = (filter: ParityManagerFiltersMarketInfo): FilterOption => ({
  id: filter.id!,
  label: filter.formattedName,
  entity: filter,
});

export default class MarketParityParityManagerController extends Controller {
  // @ts-expect-error: Type 'MarketParityParityManagerRoute' does not satisfy the constraint 'Route<unknown>'.
  declare model: ModelFrom<MarketParityParityManagerRoute>;
  // @ts-expect-error: Property 'queryParams' in type 'ParityManagerController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [
    {
      selectedMetric: { scope: 'controller' },
      selectedIssueSummaryId: { scope: 'controller' },
      channels: { scope: 'controller' },
      searchValue: { scope: 'controller' },
      fromDate: { scope: 'controller' },
      toDate: { scope: 'controller' },
      assignees: { scope: 'controller' },
      statuses: { scope: 'controller' },
      rootcauses: { scope: 'controller' },
      escalationLevels: { scope: 'controller' },
      entryModes: { scope: 'controller' },
    },
  ] as const;

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare drawer: Services['drawer'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked assignees = [];
  @tracked channels: string[] = [];
  @tracked datePickerOption = 'last_week';
  @tracked entryModes = [];
  @tracked escalationLevels = [];
  @tracked fromDate?: string;
  @tracked isSearchActive = false;
  @tracked issuesForExpandedIssueSummary: ParityManagerIssue[] = [];
  @tracked rootcauses = [];
  @tracked searchValue = '';
  @tracked selectedIssueId: string | null = null;
  @tracked selectedIssueSummaryId: string | null =
    DEFAULT_QP_VALUES.market.parity['parity-manager'].selectedIssueSummaryId;
  @tracked selectedMetric: IssueType = ISSUE_TYPES.MAJOR_OTA;
  @tracked statuses = [];
  @tracked toDate?: string;

  datePickerMinDate = dayjs(PM_DATA_START);
  lastFetchedIssueSummaryId = '';
  PM_DRAWER_ID: DrawerID = DRAWER_IDS.PM_ISSUE_DETAIL_DRAWER;
  trElement: HTMLTableRowElement | null = null;

  fetchIssuesForIssueSummaryTask = task(
    { restartable: true },
    async (issueSummaryId: string): Promise<void> => {
      const requestParams: IssueRequestParams = {
        namespace: APIGATEWAY,
        typeKey: 'paritymanager/issues',
        issue_type: this.selectedMetric,
        search_query: this.searchValue,
        extract_date_start: this.fromDateMoment.format('YYYY-MM-DD'),
        extract_date_end: this.toDateMoment.format('YYYY-MM-DD'),
        // Filters
        ota_id: this.channels,
        assignee: this.assignees,
        status: this.statuses,
        root_cause: this.rootcauses,
        escalation_level: this.escalationLevels,
        entry_mode: this.entryModes,
      };

      if (this.isMajorOtaView) {
        requestParams.subscription_id = issueSummaryId;
      } else {
        requestParams.ota_id = issueSummaryId;
        // @ts-expect-error: Type 'string | null' is not assignable to type 'string | undefined'.
        requestParams.market_id = this.hotelAndMarketService.market.id;
      }

      try {
        const issues = await this.store.query<ParityManagerIssue>(
          'parity-manager/issue',
          // @ts-expect-error: Incorrect type
          requestParams,
        );
        this.issuesForExpandedIssueSummary = [...issues];
        this.selectedIssueSummaryId = issueSummaryId;
        this.lastFetchedIssueSummaryId = issueSummaryId;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allChannels() {
    return this.filters.channels;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMaxDate() {
    return this.hotelAndMarketService.getLocalTime();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRangeOptions() {
    const todayMoment = this.hotelAndMarketService.getLocalTime();

    return [
      {
        title: '',
        options: [
          {
            text: tKey('parity_manager.datepicker.current_month'),
            id: 'current_month',
            isSelected: false,
            range: {
              start: todayMoment.startOf('month'),
              end: todayMoment,
            },
          },
          {
            text: tKey('parity_manager.datepicker.last_30_days'),
            id: 'last_30_days',
            isSelected: false,
            range: {
              start: todayMoment.subtract(30, 'day'),
              end: todayMoment,
            },
          },
          {
            text: tKey('parity_manager.datepicker.last_week'),
            id: 'last_week',
            isSelected: false,
            range: {
              start: todayMoment.subtract(6, 'day'),
              end: todayMoment,
            },
          },
          {
            // Always add a custom option to the datepicker if you can select in the calendar
            text: 'general.custom',
            id: 'custom',
            isSelected: false,
            range: {
              start: todayMoment.subtract(6, 'day'),
              end: todayMoment,
            },
          },
        ],
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filters() {
    if (this.isFilterRequestRunning) {
      return {
        channels: [],
        assignees: [],
        statuses: [],
        rootcauses: [],
        escalationLevels: [],
        entryModes: [],
        marketplaces: [],
      };
    }

    return this.model.filters.value as Filters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get fromDateMoment() {
    if (this.fromDate) {
      return this.hotelAndMarketService.toLocalTime(this.fromDate);
    }
    // Default fromDate is 6 days ago (default range is last week)
    return this.hotelAndMarketService.getLocalTime().subtract(6, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBaitAndSwitchView() {
    return this.selectedMetric === ISSUE_TYPES.BAIT_AND_SWITCH;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDataRequestRunning() {
    return this.model.data.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFilterRequestRunning() {
    return this.model.filters.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMajorOtaView() {
    return this.selectedMetric === ISSUE_TYPES.MAJOR_OTA;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issueSummaries() {
    if (this.isDataRequestRunning) {
      return [];
    }

    return this.summariesData.map(
      (issueSummary) =>
        ({
          id: issueSummary.id,
          formattedName: issueSummary.formattedName,
          amountIssues: issueSummary.amountIssues,
          escalationinfo: issueSummary.escalationinfo,
          meta: issueSummary.meta,
        }) as ParityManagerIssuesummary,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTestBookingView() {
    return this.selectedMetric === ISSUE_TYPES.TEST_BOOKING;
  }

  // Manual issues are always added to the current date (= datePickerMaxDate)
  // To avoid confusion, we only allow adding issues when we are able to see them right after they're added
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTodayInRange() {
    return this.range?.end.isSame(this.datePickerMaxDate, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get market() {
    return this.hotelAndMarketService.market;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get metrics() {
    // Static metric data (used for loading state)
    const metrics: MetricOption[] = [
      {
        id: ISSUE_TYPES.MAJOR_OTA,
        isSelected: this.selectedMetric === ISSUE_TYPES.MAJOR_OTA,
        isLoading: true,
        metricTitle: this.intl.t('reports.issues'),
        tableTitle: this.intl.t('parity_manager.major_channel_issues'),
        sidebarTitle: this.intl.t('general.hotels'),
        drawerTitle: this.intl.t('parity.contracted_issue'),
      },
      {
        id: ISSUE_TYPES.TEST_BOOKING,
        isSelected: this.selectedMetric === ISSUE_TYPES.TEST_BOOKING,
        isLoading: true,
        metricTitle: this.intl.t('parity_manager.stats.test_bookings'),
        tableTitle: this.intl.t('parity_manager.stats.test_bookings'),
        sidebarTitle: this.intl.t('parity_analytics.channel', { count: 2 }),
        drawerTitle: this.intl.t('parity.test_booking.plural', { count: 1 }),
      },
      {
        id: ISSUE_TYPES.BAIT_AND_SWITCH,
        isSelected: this.selectedMetric === ISSUE_TYPES.BAIT_AND_SWITCH,
        isLoading: true,
        metricTitle: this.intl.t('parity_manager.stats.bait_and_switch'),
        tableTitle: this.intl.t(
          'parity_manager.bait_and_switch_issues.plural',
          { count: 2 },
        ),
        sidebarTitle: this.intl.t('parity_analytics.channel', { count: 2 }),
        drawerTitle: this.intl.t(
          'parity_manager.bait_and_switch_issues.plural',
          { count: 1 },
        ),
      },
    ];

    if (this.isDataRequestRunning) {
      return metrics;
    }

    // Update metrics with dynamic data fetched from the /stats request
    metrics.forEach((metric) => {
      metric.isLoading = false;
      const stat = this.statsData?.find((st) => st.id === `${metric.id}`);
      metric.amountIssues = stat?.amountIssues;
      metric.numSubscriptions = stat?.numSubscriptions;
    });

    return metrics;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleAssignees() {
    return this.filters.assignees;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleAssigneesValue() {
    return findOptionsForValue(
      this.assignees,
      this.possibleAssigneesWithDefault,
    );
  }

  // Push the extra filter options (for assignees and statuses) to the top bar only
  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleAssigneesWithDefault() {
    return [
      {
        id: DROPDOWN_OPTION_NOT_SET,
        label: this.intl.t('parity_manager.filter.no_assignee').toString(),
      },
      {
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        id: parseInt(this.hotelAndMarketService.user?.id, 10),
        label: this.intl.t('parity_manager.filter.assigned_to_me').toString(),
      },
      // @ts-expect-error: Incorrect type
      ...this.possibleAssignees.map(mapToOption),
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleChannels() {
    return (
      this.allChannels
        ?.filter((option) => option.issueType.includes(this.selectedMetric))
        // @ts-expect-error: Incorrect type
        .map(mapToOption)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleChannelsValue() {
    return findOptionsForValue(this.channels, this.possibleChannels);
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleEntryModes() {
    // @ts-expect-error: Incorrect type
    return this.filters.entryModes.map(mapToOption);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleEntryModesValue() {
    return findOptionsForValue(this.entryModes, this.possibleEntryModes);
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleEscalationLevels() {
    // @ts-expect-error: Incorrect type
    return this.filters.escalationLevels.map(mapToOption);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleEscalationLevelsValue() {
    return findOptionsForValue(
      this.escalationLevels,
      this.possibleEscalationLevels,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleRootcauses() {
    return this.filters.rootcauses;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleRootcausesValue() {
    return findOptionsForValue(
      this.rootcauses,
      this.possibleRootcausesWithDefault,
    );
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleRootcausesWithDefault() {
    return [
      {
        label: this.isTestBookingView
          ? this.intl.t('parity_manager.filter.no_wholesaler').toString()
          : this.intl.t('parity_manager.filter.no_rootcause').toString(),

        id: DROPDOWN_OPTION_NOT_SET,
      },
      // @ts-expect-error: Incorrect type
      ...this.possibleRootcausesWithoutDefault.map(mapToOption),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleRootcausesWithoutDefault() {
    // Add root causes or wholesalers to the dropdown options depending on the selected metric
    return this.possibleRootcauses.filter(
      (option) => option.issueType === this.selectedMetric,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleStatuses() {
    return this.filters.statuses;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleStatusesValue() {
    return findOptionsForValue(this.statuses, this.possibleStatusesWithDefault);
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleStatusesWithDefault() {
    return [
      {
        id: DROPDOWN_OPTION_NOT_SET,
        label: this.intl.t('parity_manager.filter.no_status').toString(),
      },
      ...this.possibleStatuses.map((status) => ({
        id: status.id!,
        label: status.name,
      })),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryActionIcon() {
    if (this.selectedIssue?.isBaitAndSwitchIssue) {
      return 'download';
    }
    return 'mail';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryActionName() {
    if (this.selectedIssue?.isBaitAndSwitchIssue) {
      return this.intl.t('parity_manager.export_issue_pdf');
    }
    return this.intl.t('parity_manager.contact_hotel');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get range() {
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rootCauseOrWholesalerPlaceholder() {
    return this.isTestBookingView
      ? 'parity_manager.wholesaler.placeholder'
      : tKey('general.dropdown.select_root_cause');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rootCauseOrWholesalerPrefix() {
    return this.isTestBookingView
      ? tKey('parity_manager.wholesaler.count')
      : tKey('parity_manager.rootcause.count');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rootCauseOrWholesalerTitle() {
    return this.isTestBookingView
      ? 'parity_manager.stats.wholesaler'
      : 'parity_manager.rootcause';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedIssue() {
    return this.issuesForExpandedIssueSummary.find(
      (issue) => issue.id === this.selectedIssueId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedIssueSummary() {
    return this.issueSummaries.find(
      (issueSummary) => issueSummary.id === this.selectedIssueSummaryId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMetricObject() {
    return this.metrics.find((metric) => metric.isSelected);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get statsData() {
    return this.model.data.value?.stats;
  }

  get summariesData(): ParityManagerIssuesummary[] {
    // Required for the render-modifiers in the template
    if (this.isDataRequestRunning) {
      return [];
    }

    return this.model.data.value?.summaries ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timezone() {
    return this.hotelAndMarketService.timezone;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get toDateMoment() {
    return this.hotelAndMarketService.toLocalTimeWithFallback(this.toDate);
  }

  willDestroy(...args: Parameters<Component['willDestroy']>): void {
    super.willDestroy(...args);

    // Clean up saved elements to avoid memory leaks
    this.trElement = null;
  }

  @action addIssue(): void {
    this.router.transitionTo('market.parity.parity-manager.addissue');
  }

  @action addRowHighlight(event: Event): void {
    if (this.trElement) {
      // Remove the highlight from the previously selected row
      this.trElement.removeAttribute('selected');
    }
    // Add a highlight to the currently selected row
    this.trElement = (event.target as HTMLElement).closest('tr');
    this.trElement?.setAttribute('selected', 'selected');
  }

  @action collapseDrawer(): void {
    this.removeRowHighlight();
    this.drawer.collapseDrawer(this.PM_DRAWER_ID);
  }

  @action exportPDF(): void {
    const url = formatUrl(
      '/apigateway/v1/app/paritymanager/export_issue',
      {
        market_id: this.market.id,
        issue_type: this.selectedMetric,
        issue_id: this.selectedIssue?.id,
      },
      this.ajax.host,
    );

    openUrl(url);
  }

  @action handleChangeForType(
    type: string,
    totalNumberOfOptions: number,
    value: FilterOption[],
  ): void {
    if (value.length === totalNumberOfOptions) {
      // Clear filters if all options are selected
      this.router.transitionTo({ queryParams: { [type]: [] } });
    } else {
      const filterValueIds = value.map((option) => option.id);
      this.usageAnalytics.track('Filter Applied', {
        eventTrigger: 'filter-bar',
        filterType: type,
        filterValueIds,
      });

      this.router.transitionTo({
        queryParams: { [type]: filterValueIds },
      });
    }
  }

  @action handleExport(): void {
    const requestParams: ExportRequestParams = {
      report: '1',
      // @ts-expect-error: Type 'string | null' is not assignable to type 'string'.
      market_id: this.market.id,
      issue_type: this.selectedMetric,
      search_query: this.searchValue,
      // Filters
      'ota_id[]': this.channels,
      'assignee[]': this.assignees,
      'status[]': this.statuses,
      'root_cause[]': this.rootcauses,
      'escalation_level[]': this.escalationLevels,
      'entry_mode[]': this.entryModes,
    };

    if (this.fromDate) {
      requestParams.extract_date_start = this.fromDate;
    }

    if (this.toDate) {
      requestParams.extract_date_end = this.toDate;
    }

    this.usageAnalytics.track('Data Exported', {
      eventTrigger: 'filter-bar',
    });

    const basicExportUrl = formatUrl(
      '/apigateway/v1/app/paritymanager/export',
      requestParams,
      this.ajax.host,
    );

    logReportInput(basicExportUrl, this.access.isStaffUser);

    openUrl(basicExportUrl);
  }

  @action onIssueSummariesDataUpdate(): void {
    // Always visualize an issueSummary in the table: the first one by default or the issueSummary ID from the url (if it's valid)
    if (
      this.selectedIssueSummaryId &&
      this.issueSummaries
        .map((issueSummary) => issueSummary.id)
        .includes(this.selectedIssueSummaryId)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchIssuesForIssueSummaryTask.perform(this.selectedIssueSummaryId);
    } else if (this.issueSummaries?.[0]) {
      // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchIssuesForIssueSummaryTask.perform(this.issueSummaries[0].id);
    }
  }

  @action onIssueSummaryClick(issueSummaryId: string): void {
    // Don't fetch the issues again when clicking the currently selected issueSummary
    if (this.lastFetchedIssueSummaryId !== issueSummaryId) {
      // Collapse the drawer with information about other issuesummaries
      this.collapseDrawer();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchIssuesForIssueSummaryTask.perform(issueSummaryId);
    }
  }

  @action onIssueUploadClicked(
    subscriptionId: number,
    issueId: string,
    issueType: number,
  ): void {
    this.router.transitionTo('market.parity.parity-manager.issueupload', {
      queryParams: {
        subscriptionId,
        issueId,
        issueType,
      },
    });
  }

  @action onRowClick(row: {
    event: Event;
    rowValue: CustomEmberTableRow;
  }): void {
    const { event } = row;
    const isEventTriggeredFromQuickActions =
      (event.target as HTMLElement).closest('.stop-propagation-to-drawer') !==
      null;
    const isRedirectQuickAction =
      (event.target as HTMLElement).closest('.propagate-to-drawer') !== null;

    // If we click any quick action besides the redirect, the drawer should not expand
    if (!isEventTriggeredFromQuickActions || isRedirectQuickAction) {
      this.addRowHighlight(event);
      this.selectedIssueId = row.rowValue['id'] as string;
      this.drawer.expandDrawer(this.PM_DRAWER_ID);

      if (isRedirectQuickAction) {
        this.trackIssueTableAction('Details Viewed');
      } else {
        this.trackIssueTableAction('Side Panel Triggered');
      }
    }
  }

  @action primaryAction(): void {
    if (
      this.selectedIssue?.isMajorOTAIssue ||
      this.selectedIssue?.isTestBookingIssue
    ) {
      this.redirectToMailRoute();
    }
    if (this.selectedIssue?.isBaitAndSwitchIssue) {
      this.exportPDF();
    }
  }

  @action rangeChanged(range: {
    end: dayjs.Dayjs | null;
    start: dayjs.Dayjs;
  }): void {
    if (range.start) {
      this.fromDate = range.start.format('YYYY-MM-DD');
    }
    if (range.end) {
      this.toDate = range.end.format('YYYY-MM-DD');
    } else {
      this.toDate = range.start.format('YYYY-MM-DD');
    }
  }

  @action redirectToMailRoute(): void {
    if (!this.selectedIssue) {
      return;
    }

    this.trackSidePanelAction('Hotel Contacted');

    const { issueType, id, subscriptionId } = this.selectedIssue;
    this.router.transitionTo('market.parity.parity-manager.mail', {
      queryParams: {
        subscriptionId,
        issueId: id,
        issueType,
        mailType: MAIL_TYPE.HOTEL,
      },
    });
  }

  @action removeRowHighlight(): void {
    if (this.trElement) {
      // Remove the row highlight and the reference to the row
      this.trElement.removeAttribute('selected');
      this.trElement = null;
    }
  }

  @action resetSearchValue(): void {
    this.searchValue = '';
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  selectsBuildLabelWithCount(labelTranslationKey: string, count: number) {
    return this.intl.t(labelTranslationKey, { count });
  }

  @action setSelectedMetric(selectedMetric: IssueType): void {
    this.collapseDrawer();
    // Clear the selected issueSummary when changing metric
    this.selectedIssueSummaryId = null;

    this.usageAnalytics.track('View Changed', {
      eventTrigger: 'switch-buttons',
      viewType: ISSUE_TYPE_PRETTY_NAMES[selectedMetric],
    });

    this.router.transitionTo({
      queryParams: { selectedMetric },
    });
  }

  @action toggleSearch(isActive: boolean): void {
    this.isSearchActive = isActive;
  }

  @action trackIssueTableAction(event: string): void {
    this.usageAnalytics.track(event, {
      eventTrigger: 'issue-table',
      viewType: ISSUE_TYPE_PRETTY_NAMES[this.selectedMetric],
      channelName: this.selectedIssue?.formattedChannel,
      daysSinceShopped: this.selectedIssue?.daysSinceShopped,
    });
  }

  @action trackSidePanelAction(event: string): void {
    this.usageAnalytics.track(event, {
      eventTrigger: 'side-panel',
      viewType: ISSUE_TYPE_PRETTY_NAMES[this.selectedMetric],
      channelName: this.selectedIssue?.formattedChannel,
      daysSinceShopped: this.selectedIssue?.daysSinceShopped,
    });
  }

  @action
  async updateEscalationLevelForSubscription(
    subscriptionId: number,
  ): Promise<void> {
    const summaryRequestParams = {
      namespace: APIGATEWAY,
      typeKey: 'paritymanager/stats',
      market_id: this.market.id,
      subscription_id: subscriptionId,
      issue_type: this.selectedMetric,
      search_query: this.searchValue,
      extract_date_start: this.fromDateMoment.format('YYYY-MM-DD'),
      extract_date_end: this.toDateMoment.format('YYYY-MM-DD'),
    };

    try {
      const response: StatsResult = await this.ajax.request(
        formatUrl(
          '/apigateway/v1/app/paritymanager/stats',
          summaryRequestParams,
          this.ajax.host,
        ),
        { method: 'GET' },
      );

      // Workaround so the issuestats don't get filtered as a side-effect
      delete response.issuestats;
      // Update the issuesummary and escalationinfo for the selected subscription ID in the store.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload(response);
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  @action updateSearchValue(value: string): void {
    if (this.searchValue !== value) {
      this.router.transitionTo({ queryParams: { searchValue: value } });
    }
  }
}
